/**
 * Call metrics widget — total calls, average duration, inbound vs outbound (Abandoned-in-queue tile layout).
 */
import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { buildCallHistoryParams, formatDurationSeconds, type WidgetFilterConfig } from '../../lib/widgetScope';

interface CallStats {
  total_calls: number;
  avg_duration: number;
  inbound_count: number;
  outbound_count: number;
}

interface WidgetCallMetricsProps {
  config?: WidgetFilterConfig;
}

export default function WidgetCallMetrics({ config }: WidgetCallMetricsProps) {
  const { getToken } = useAuth();
  const [stats, setStats] = useState<CallStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      const params = buildCallHistoryParams(config);
      setLoading(true);
      try {
        const res = await fetch(`${base}/api/calls/stats?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError((data as { error?: string }).error || `HTTP ${res.status}`);
          setStats(null);
          return;
        }
        const data = await res.json();
        setStats({
          total_calls: data.total_calls ?? 0,
          avg_duration: data.avg_duration ?? 0,
          inbound_count: data.inbound_count ?? 0,
          outbound_count: data.outbound_count ?? 0,
        });
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, config?.scope, config?.campaign_id, config?.ipscape_agent_id, config?.date_range]);

  const tiles = stats
    ? [
        { label: 'Total calls', value: String(stats.total_calls) },
        { label: 'Avg duration', value: formatDurationSeconds(stats.avg_duration) },
        { label: 'Inbound', value: String(stats.inbound_count) },
        { label: 'Outbound', value: String(stats.outbound_count) },
      ]
    : [];

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: '0.5rem 0.75rem',
        containerType: 'size',
      }}
    >
      {loading && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 'clamp(0.75rem, 2cqi, 1rem)' }}>
          Loading…
        </div>
      )}
      {error && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--error)', fontSize: 'clamp(0.75rem, 2cqi, 1rem)', textAlign: 'center' }}>
          {error}
        </div>
      )}
      {!loading && !error && stats !== null && (
        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'clamp(0.5rem, 2cqi, 1rem)',
            minHeight: 0,
            alignContent: 'stretch',
          }}
        >
          {tiles.map((t) => (
            <div
              key={t.label}
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: 'clamp(0.5rem, 2cqi, 1.25rem)',
                background: 'var(--bg)',
                borderRadius: 8,
                textAlign: 'center',
              }}
            >
              <div style={{ color: 'var(--text-muted)', fontSize: 'clamp(0.65rem, 1.8cqi, 0.9rem)', marginBottom: '0.25rem' }}>{t.label}</div>
              <div style={{ fontSize: 'clamp(1.1rem, 5cqi, 2.5rem)', fontWeight: 700 }}>{t.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
